// Phase SA-4/5 — Brief §03 A3: Odds range analysis.
//
// 4-col grid + auto-generated insight line below:
//   Band | Legs | Win rate | Process accuracy
//
// Reads from user_betting_profile.odds_range_performance (JSONB):
//   { '<band_slug>': { legs, wins, rate, process_accuracy } }
//
// Rules (brief verbatim):
//   • 7 fixed bands in fixed ascending order.
//   • Min 5 legs per band before showing rate → 'Insufficient data'.
//   • Empty: all bands render with '—', insight reads
//     "Build your profile to see where your odds edge lives."
//   • Two-sentence insight when there's enough data:
//       "Your highest win rate is in the {label} range ({pct}%).
//        Your process accuracy drops below 50% above {threshold}×."
//   • No Pro gate — pure data display, free + Pro both see all
//     columns.

import type { OddsRangeEntry } from "../../slips/useBettingProfile.ts";

export const ODDS_BANDS: ReadonlyArray<{
  slug: string;
  label: string;
  upper: number; // for the process-drop "above N×" insight
}> = [
  { slug: "band_120_150", label: "1.20–1.50", upper: 1.5 },
  { slug: "band_151_180", label: "1.51–1.80", upper: 1.8 },
  { slug: "band_181_220", label: "1.81–2.20", upper: 2.2 },
  { slug: "band_221_280", label: "2.21–2.80", upper: 2.8 },
  { slug: "band_281_350", label: "2.81–3.50", upper: 3.5 },
  { slug: "band_351_500", label: "3.51–5.00", upper: 5.0 },
  { slug: "band_500_plus", label: "5.00+", upper: Infinity },
];

const MIN_LEGS_PER_BAND = 5;

function pct(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return `${Math.round(n * 100)}%`;
}

export interface OddsRangeAnalysisProps {
  data: Record<string, OddsRangeEntry>;
}

/** Pure derivation — the band with the highest win rate among
 * bands that have at least `MIN_LEGS_PER_BAND` legs. Returns null
 * if no band qualifies. */
export function peakRateBand(
  data: Record<string, OddsRangeEntry>,
): { slug: string; label: string; rate: number } | null {
  let best: { slug: string; label: string; rate: number } | null = null;
  for (const { slug, label } of ODDS_BANDS) {
    const row = data[slug];
    if (!row || row.legs < MIN_LEGS_PER_BAND) continue;
    if (row.rate == null || !Number.isFinite(row.rate)) continue;
    if (!best || row.rate > best.rate) {
      best = { slug, label, rate: row.rate };
    }
  }
  return best;
}

/** Pure derivation — the lowest-upper band where process accuracy
 * drops below 50% (among bands with at least MIN_LEGS_PER_BAND
 * legs). The brief insight reads "above {threshold}×" — we return
 * the upper bound of the PREVIOUS band, i.e. the threshold above
 * which the drop kicks in. Returns null if no band qualifies. */
export function processDropThreshold(
  data: Record<string, OddsRangeEntry>,
): { slug: string; label: string; threshold: number } | null {
  for (let i = 0; i < ODDS_BANDS.length; i++) {
    const band = ODDS_BANDS[i];
    if (!band) continue;
    const row = data[band.slug];
    if (!row || row.legs < MIN_LEGS_PER_BAND) continue;
    if (row.process_accuracy == null) continue;
    if (row.process_accuracy >= 0.5) continue;
    // Found the first band where process accuracy < 50%.
    // The "threshold" is the lower bound of THIS band (i.e. odds
    // > the upper of the previous band).
    const prev = i > 0 ? ODDS_BANDS[i - 1] : null;
    return {
      slug: band.slug,
      label: band.label,
      // For the first band (no prev), the threshold is the band's
      // own lower implicit bound — practically use prev?.upper for
      // human-readable insights. If first band itself drops, fall
      // back to its own upper bound.
      threshold: prev ? prev.upper : band.upper,
    };
  }
  return null;
}

export function OddsRangeAnalysis({
  data,
}: OddsRangeAnalysisProps): JSX.Element {
  const totalLegs = ODDS_BANDS.reduce(
    (sum, b) => sum + (data[b.slug]?.legs ?? 0),
    0,
  );
  const peak = peakRateBand(data);
  const drop = processDropThreshold(data);

  return (
    <div data-testid="sa-odds-range">
      <div
        className="sa-odds-range"
        role="table"
        aria-label="Odds range analysis"
      >
        <div className="sa-mm-head" role="columnheader">
          Band
        </div>
        <div className="sa-mm-head" role="columnheader">
          Legs
        </div>
        <div className="sa-mm-head" role="columnheader">
          Win rate
        </div>
        <div className="sa-mm-head" role="columnheader">
          Process accuracy
        </div>

        {ODDS_BANDS.map(({ slug, label }) => {
          const row = data[slug];
          const insufficient = !row || row.legs < MIN_LEGS_PER_BAND;
          return (
            <BandRow
              key={slug}
              slug={slug}
              label={label}
              row={row}
              insufficient={insufficient}
            />
          );
        })}
      </div>

      <p
        data-testid="sa-or-insight"
        style={{
          marginTop: 12,
          fontFamily: "var(--sa-font-body)",
          fontSize: 13.5,
          color: "var(--sa-fg-secondary)",
          lineHeight: 1.55,
        }}
      >
        {renderInsight(totalLegs, peak, drop)}
      </p>
    </div>
  );
}

function BandRow({
  slug,
  label,
  row,
  insufficient,
}: {
  slug: string;
  label: string;
  row: OddsRangeEntry | undefined;
  insufficient: boolean;
}): JSX.Element {
  const legs = row?.legs ?? 0;
  const rate = row?.rate ?? null;
  const process = row?.process_accuracy ?? null;

  return (
    <>
      <div
        className="sa-mm-cell"
        data-testid={`sa-or-${slug}-label`}
        role="cell"
        style={{ fontWeight: 500, fontFamily: "var(--sa-font-mono)" }}
      >
        {label}
      </div>
      <div
        className="sa-mm-cell"
        data-testid={`sa-or-${slug}-legs`}
        role="cell"
        data-empty={legs === 0 ? "true" : undefined}
      >
        {legs === 0 ? "—" : legs}
      </div>
      <div
        className="sa-mm-cell"
        data-testid={`sa-or-${slug}-rate`}
        role="cell"
        data-empty={insufficient ? "true" : undefined}
      >
        {insufficient ? (legs === 0 ? "—" : "Insufficient data") : pct(rate)}
      </div>
      <div
        className="sa-mm-cell"
        data-testid={`sa-or-${slug}-process`}
        role="cell"
        data-empty={insufficient ? "true" : undefined}
      >
        {insufficient ? (legs === 0 ? "—" : "Insufficient data") : pct(process)}
      </div>
    </>
  );
}

function renderInsight(
  totalLegs: number,
  peak: ReturnType<typeof peakRateBand>,
  drop: ReturnType<typeof processDropThreshold>,
): JSX.Element {
  if (totalLegs === 0) {
    return (
      <span data-testid="sa-or-insight-empty">
        Build your profile to see where your odds edge lives.
      </span>
    );
  }
  if (!peak && !drop) {
    return (
      <span data-testid="sa-or-insight-thin">
        {`More legs needed across the bands to surface a sharp insight. Each band needs at least ${MIN_LEGS_PER_BAND} legs.`}
      </span>
    );
  }
  return (
    <>
      {peak ? (
        <span data-testid="sa-or-insight-peak">
          {`Your highest win rate is in the ${peak.label} range (${Math.round(peak.rate * 100)}%). `}
        </span>
      ) : null}
      {drop ? (
        <span data-testid="sa-or-insight-drop">
          {drop.threshold === Infinity
            ? `Your process accuracy drops below 50% in the ${drop.label} range.`
            : `Your process accuracy drops below 50% above ${drop.threshold.toFixed(2)}×.`}
        </span>
      ) : null}
    </>
  );
}
